/**
 * Maps a purchased SkuRun to the in-app route that VIEWS its result (so "My Rites"
 * opens the result instead of the buy page).
 *
 * KEEP IN SYNC with the server copy: services/astro-bot/src/api/routes/me-purchases.ts
 * (deriveResultRef). If you add a jobType here, add it there too — Purchases uses
 * the server ref, the Me-overview rites strip uses this client copy.
 */
import { getSku } from '@wds/astro-bot-shared/catalog';

export interface ResultRef {
  kind: 'compat' | 'avatar' | 'past_lives' | 'text' | 'couple' | 'natal';
  id: string;
}

/** Classify a run's stored output into a viewable result pointer (or null). */
export function deriveResultRef(jobType: string | undefined, output: unknown, skuRunId?: string): ResultRef | null {
  const o = (output ?? {}) as Record<string, unknown>;
  const packIds = Array.isArray(o.packIds) ? (o.packIds as unknown[]) : [];
  const runIds = Array.isArray(o.runIds) ? (o.runIds as unknown[]) : [];
  // Known limitation: zodiac_collection (jobType 'pack') stores 12 packIds; we
  // open packIds[0] only — there is no multi-pack collection viewer yet.
  if (jobType === 'compat_check' && typeof o.compatCheckId === 'string') {
    return { kind: 'compat', id: o.compatCheckId };
  }
  if (jobType === 'couple_portrait' && typeof o.couplePortraitId === 'string') {
    return { kind: 'couple', id: o.couplePortraitId };
  }
  if ((jobType === 'pack' || jobType === 'hd_single') && typeof packIds[0] === 'string') {
    return { kind: 'avatar', id: packIds[0] };
  }
  if (jobType === 'past_lives' && typeof runIds[0] === 'string') {
    return { kind: 'past_lives', id: runIds[0] };
  }
  // natal_bundle stores no markdown — its 3 themes open on the natal-chart screen.
  if (jobType === 'natal_bundle') {
    return { kind: 'natal', id: skuRunId ?? '' };
  }
  // Any purchase whose output stored markdown reopens as a text result — covers the
  // text SKUs, past_lives_text, and any legacy/renamed SKU (jobType undefined).
  if (typeof o.markdown === 'string' && skuRunId) {
    return { kind: 'text', id: skuRunId };
  }
  return null;
}

/** Convenience for a run row that carries skuCode + raw output (e.g. useSkuRuns). */
export function resultRefForRun(skuCode: string, output: unknown): ResultRef | null {
  return deriveResultRef(getSku(skuCode)?.jobType, output);
}

/** The Mini App route that renders a given result, or null when not viewable. */
export function resultRoute(ref: ResultRef | null | undefined): string | null {
  if (!ref) return null;
  switch (ref.kind) {
    case 'compat':
      return `/compat/${ref.id}`;
    case 'avatar':
      return `/me/avatar-pack/${ref.id}`;
    case 'past_lives':
      return `/me/past-lives/${ref.id}`;
    case 'text':
      return `/me/result/${ref.id}`;
    case 'couple':
      return `/compat/${ref.id}`;
    case 'natal':
      return `/me/natal-chart`;
  }
}
